import {ExpandLess, ExpandMore} from '@mui/icons-material';
import {Box, Collapse, Grid, List, ListItemButton, ListItemText, Switch, Typography} from '@mui/material';
import {makeStyles} from '@mui/styles';
import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useFormContext, useWatch} from 'react-hook-form';
import Select from 'react-select';
import {i18n} from 'src/i18n';
import {
  formatAlternativeTreePath,
  getMultipleChoiceTreeDisplayLabel,
  resolveAlternativeTreeDisplayLevels,
} from 'src/modules/shared/alternativeTreeDisplay';
import FormErrors from 'src/view/shared/form/formErrors';
import {useReactSelectKeyboard} from 'src/view/shared/form/items/shared/reactSelectKeyboard';
import {
  components as materialUiComponents,
  styles as materialUiStyles,
} from 'src/view/shared/form/items/shared/reactSelectMaterialUi';
import TextAreaFormItem from './TextAreaFormItem';
import {DisabledItem, LabelTypography} from './shared/formItemWrappers';

const useStyles = makeStyles(materialUiStyles as any);

function filterTree(nodes, term) {
  if (!term) return nodes;
  const lower = term.toLowerCase();
  return (nodes || [])
    .map((node) => {
      const match = node.label.toLowerCase().includes(lower);
      const children = node.children ? filterTree(node.children, term) : [];
      if (match || children.length > 0) {
        return {...node, children};
      }
      return null;
    })
    .filter(Boolean);
}

function flattenVisibleTree(nodes, openNodes, forceOpen, path = '') {
  const result: any[] = [];
  (nodes || []).forEach((opt, i) => {
    const currentKey = path ? `${path}-${i}` : `${i}`;
    const isLeaf = !opt.children || opt.children.length === 0;
    const isOpen = forceOpen || openNodes.has(currentKey);
    result.push({opt, currentKey, isLeaf, isOpen});
    if (!isLeaf && isOpen) {
      result.push(...flattenVisibleTree(opt.children, openNodes, forceOpen, currentKey));
    }
  });
  return result;
}

function SelectTreeFormItem(props) {
  const {
    label,
    name,
    hint,
    options,
    required,
    mode,
    placeholder,
    isClearable,
    externalErrorMessage,
    defaultValue,
    disabled,
    nameOthers,
    allowOthersOption,
    selectAllButton,
    shrink = true,
    noAnswerText = 'Sin Respuesta',
    alternativeTreePregunta,
    pathDisplayViewKey = 'disabledInput',
  } = props;

  const {
    register,
    formState: {touchedFields, isSubmitted, errors},
    setValue,
    control,
  } = useFormContext();

  const errorMessage = FormErrors.errorMessage(name, errors, touchedFields, isSubmitted, externalErrorMessage);
  const {menuIsOpen, onMenuOpen, onMenuClose, setMenuIsOpen} = useReactSelectKeyboard();

  const originalValue = useWatch({name, control, defaultValue});
  const othersValue = useWatch({name: nameOthers, control});

  useEffect(() => {
    register(name);
    setValue(name, originalValue);
  }, []);

  const [openNodes, setOpenNodes] = useState(new Set());
  const [focusedKey, setFocusedKey] = useState('0');
  const [inputValue, setInputValue] = useState('');
  const itemRefs = useRef<Record<string, HTMLElement | null>>({});
  const focusedKeyRef = useRef(focusedKey);
  focusedKeyRef.current = focusedKey;

  const expandPath = (keyPath) => {
    const updated = new Set();
    let current = '';
    keyPath.split('-').forEach((part, i) => {
      current = i === 0 ? part : `${current}-${part}`;
      updated.add(current);
    });
    setOpenNodes(updated);
  };

  const toggleNode = (keyPath) => {
    const isOpen = openNodes.has(keyPath);
    if (isOpen) {
      const updated = new Set([...openNodes].filter((k: any) => !k.startsWith(keyPath)));
      setOpenNodes(updated);
    } else {
      expandPath(keyPath);
    }
  };

  const handleSelectOne = (val, item) => {
    setValue(name, val, {shouldValidate: true});
    props.onChange && props.onChange(val);
    props.onChangeItem && props.onChangeItem(item);
    setMenuIsOpen(false);
    setInputValue('');
  };

  const filteredOptions = useMemo(() => filterTree(options, inputValue), [options, inputValue]);
  const forceOpenFiltered = Boolean(inputValue);
  const visibleNodes = useMemo(
    () => flattenVisibleTree(filteredOptions, openNodes, forceOpenFiltered),
    [filteredOptions, openNodes, forceOpenFiltered],
  );
  const visibleNodesRef = useRef(visibleNodes);
  visibleNodesRef.current = visibleNodes;

  useEffect(() => {
    if (!visibleNodes.length) return;
    if (!visibleNodes.some((node) => node.currentKey === focusedKey)) {
      setFocusedKey(visibleNodes[0].currentKey);
    }
  }, [visibleNodes, focusedKey]);

  useEffect(() => {
    if (!menuIsOpen || !focusedKey) return;
    itemRefs.current[focusedKey]?.scrollIntoView({block: 'nearest'});
  }, [focusedKey, menuIsOpen]);

  const handleTreeKeyDown = (event) => {
    if (event.key === 'Enter' && !menuIsOpen) {
      event.preventDefault();
      setMenuIsOpen(true);
      return;
    }
    if (event.key === 'Escape' && menuIsOpen) {
      event.preventDefault();
      setMenuIsOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!menuIsOpen) {
        setMenuIsOpen(true);
        return;
      }
      const nodes = visibleNodesRef.current;
      if (!nodes.length) return;
      const idx = nodes.findIndex((node) => node.currentKey === focusedKeyRef.current);
      const nextIndex =
        event.key === 'ArrowDown'
          ? Math.min((idx < 0 ? -1 : idx) + 1, nodes.length - 1)
          : Math.max((idx < 0 ? 0 : idx) - 1, 0);
      setFocusedKey(nodes[nextIndex].currentKey);
      return;
    }
    if (!menuIsOpen) return;

    if (event.key === 'Enter' || (event.key === ' ' && !inputValue)) {
      event.preventDefault();
      const node = visibleNodesRef.current.find((item) => item.currentKey === focusedKeyRef.current);
      if (!node) return;
      if (node.isLeaf) {
        handleSelectOne(node.opt.value, node.opt);
      } else {
        toggleNode(node.currentKey);
      }
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      const node = visibleNodesRef.current.find((item) => item.currentKey === focusedKeyRef.current);
      if (node && !node.isLeaf && !node.isOpen) {
        toggleNode(node.currentKey);
      }
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      const node = visibleNodesRef.current.find((item) => item.currentKey === focusedKeyRef.current);
      if (node && !node.isLeaf && node.isOpen) {
        toggleNode(node.currentKey);
      }
    }
  };

  const valueOne = () => {
    const {options} = props;
    if (originalValue) {
      let optionByValue = findOptionByValue(options, originalValue);
      if (!optionByValue && originalValue) {
        optionByValue = {
          value: 'others',
          label: 'Otro: ' + originalValue,
          children: [],
        };
      }
      return optionByValue;
    }
    return null;
  };

  function findOptionByValue(list, val) {
    for (let opt of list) {
      if (opt.value === val) return opt;
      if (opt.children) {
        const child = findOptionByValue(opt.children, val);
        if (child) return child;
      }
    }
    return null;
  }

  function computeTreeValue(list, val) {
    for (let opt of list) {
      if (opt.value === val) return opt.label;
      if (opt.children && opt.children.length > 0) {
        const childPath = computeTreeValue(opt.children, val);
        if (childPath) {
          const path = opt.label + ' / ' + childPath;
          return path;
        }
      }
    }
    return null;
  }

  const renderTreeOptions = (opts, level = 0, path = '', menuProps) => {
    const {selectProps} = menuProps;
    return (
      <List disablePadding>
        {opts.map((opt, i) => {
          const currentKey = path ? `${path}-${i}` : `${i}`;
          const isOpen = forceOpenFiltered || openNodes.has(currentKey);
          const isLeaf = !opt.children || opt.children.length === 0;
          const isFocused = focusedKey === currentKey;

          return (
            <Box key={opt.value}>
              <ListItemButton
                dense
                ref={(el) => {
                  itemRefs.current[currentKey] = el;
                }}
                selected={isFocused}
                sx={{
                  pl: level * 2 + 1,
                  py: 0.5,
                  bgcolor: isFocused ? 'action.hover' : undefined,
                }}
                onMouseEnter={() => setFocusedKey(currentKey)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isLeaf) {
                    handleSelectOne(opt.value, opt);
                    selectProps.onMenuClose();
                  } else {
                    toggleNode(currentKey);
                  }
                }}>
                <ListItemText
                  primaryTypographyProps={{
                    fontSize: 14,
                    color: 'text.primary',
                  }}
                  primary={opt.label}
                />
                {!isLeaf && (isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />)}
              </ListItemButton>

              {opt.children && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  {renderTreeOptions(opt.children, level + 1, currentKey, menuProps)}
                </Collapse>
              )}
            </Box>
          );
        })}
      </List>
    );
  };

  const classes = useStyles();

  const marginOptions = {
    normal: {marginTop: '16px', marginBottom: '8px'},
    none: {marginTop: '0px', marginBottom: '0px'},
  };

  const controlStyles = {
    ...materialUiStyles,
    container: (provided) => ({
      ...provided,
      width: '100%',
      ...marginOptions[props.margin || 'none'],
      ...props.containerStyle,
    }),
    control: (provided) => ({
      ...provided,
      borderColor: errorMessage ? 'red' : undefined,
      ...props.controlStyle,
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      maxHeight: 400,
      overflowY: 'auto',
    }),
    /** El menu portal solo afecta cuando el props menuPortalTarget={document.body} está activo */
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
      //zoom: props.menuZoom || 0.8,
    }),
  };

  function applyPathDisplayLevels(fullPath: string | null) {
    if (!fullPath || !alternativeTreePregunta?.alternativeTree) {
      return fullPath;
    }
    const levels = resolveAlternativeTreeDisplayLevels(alternativeTreePregunta, pathDisplayViewKey);
    return formatAlternativeTreePath(fullPath, levels);
  }

  let computedValue = computeTreeValue(options, originalValue);
  computedValue = applyPathDisplayLevels(computedValue);
  if (!computedValue && originalValue && originalValue !== 'others') {
    const treeLabel = getMultipleChoiceTreeDisplayLabel(
      options,
      originalValue,
      alternativeTreePregunta,
      pathDisplayViewKey,
      (list, val) => computeTreeValue(list, val),
    );
    if (treeLabel) {
      computedValue = treeLabel;
    }
  }
  if (!computedValue && originalValue && originalValue !== 'others') {
    computedValue = 'Otros: ' + originalValue;
  }
  if (disabled) {
    return (
      <DisabledItem
        noAnswerText={noAnswerText}
        label={label}
        show={Boolean(computedValue)}
        item={
          <>
            <LabelTypography>{computedValue}</LabelTypography>
            {allowOthersOption && originalValue === 'others' && (
              <Box
                sx={{
                  p: 1,
                  mb: 1,
                  border: '1px solid lightgray',
                  borderRadius: '5px',
                }}>
                <Typography
                  component="pre"
                  variant="subtitle2"
                  sx={{
                    fontWeight: 'inherit',
                    fontSize: 'inherit',
                    fontFamily: 'inherit',
                    whiteSpace: 'pre-wrap',
                  }}>
                  {othersValue}
                </Typography>
              </Box>
            )}
          </>
        }
      />
    );
  }

  return (
    <>
      {selectAllButton && mode === 'multiple' && (
        <Grid lg={12} style={{...marginOptions[props.margin || 'normal']}}>
          <label>Seleccionar Todos</label>
          <Switch
            onChange={(e) =>
              e.target.checked
                ? setValue(
                    name,
                    options.map((o) => o.value),
                    {shouldValidate: true},
                  )
                : setValue(name, [], {shouldValidate: true})
            }
          />
        </Grid>
      )}

      <div
        style={{display: 'flex', alignItems: 'start', flex: 1}}
        onClick={(event) => {
          props.onClick && props.onClick(event);
        }}>
        <Select
          menuPortalTarget={props.menuPortalTarget}
          styles={controlStyles}
          classes={classes}
          value={valueOne()}
          menuIsOpen={menuIsOpen}
          onMenuOpen={() => {
            onMenuOpen();
            const first = visibleNodesRef.current[0];
            if (first) setFocusedKey(first.currentKey);
          }}
          onMenuClose={() => {
            onMenuClose();
            setInputValue('');
          }}
          onKeyDown={handleTreeKeyDown}
          inputValue={inputValue}
          onInputChange={(value, meta) => {
            if (meta.action === 'input-change') {
              setInputValue(value);
            }
            if (meta.action === 'menu-close') {
              setInputValue('');
            }
          }}
          formatOptionLabel={(option, {context}) => {
            if (context === 'value' && !props.displayLeafValue) {
              return <span>{computedValue}</span>;
            }
            return <span>{option.label}</span>;
          }}
          inputId={name}
          TextFieldProps={{
            label,
            required,
            variant: 'outlined',
            fullWidth: true,
            size: 'small',
            error: Boolean(errorMessage),
            helperText: errorMessage || hint,
            InputLabelProps: {
              shrink: shrink,
            },
          }}
          components={{
            ...materialUiComponents,
            MenuList: (menuProps) => (
              <div style={{padding: 6}}>{renderTreeOptions(filteredOptions, 0, '', menuProps)}</div>
            ),
          }}
          placeholder={placeholder || ''}
          isClearable={isClearable}
          onChange={(selected) => {
            if (!selected) {
              handleSelectOne(null, null);
              return;
            }
          }}
          loadingMessage={() => i18n('autocomplete.loading')}
          noOptionsMessage={() => i18n('autocomplete.noOptions')}
        />
        {props.endButtonMargin && (
          <div
            style={{
              width: props.endButtonMargin,
            }}
          />
        )}
      </div>

      {allowOthersOption && originalValue === 'others' && (
        <>
          <TextAreaFormItem
            rows={3}
            disabled={false}
            name={nameOthers}
            placeholder="Si tu respuesta es otros, por favor especifica"
          />
        </>
      )}
    </>
  );
}

SelectTreeFormItem.defaultProps = {
  required: false,
  isClearable: true,
  selectAllButton: true,
};

SelectTreeFormItem.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  label: PropTypes.string,
  hint: PropTypes.string,
  required: PropTypes.bool,
  externalErrorMessage: PropTypes.string,
  mode: PropTypes.string,
  isClearable: PropTypes.bool,
  defaultValue: PropTypes.any,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onChangeItem: PropTypes.func,
  margin: PropTypes.string,
  nameOthers: PropTypes.string,
  allowOthersOption: PropTypes.bool,
  containerStyle: PropTypes.object,
  controlStyle: PropTypes.object,
  selectAllButton: PropTypes.bool,
  endButtonMargin: PropTypes.string,
  shrink: PropTypes.bool,
  menuPortalTarget: PropTypes.object,
  sort: PropTypes.func,
  onFocus: PropTypes.func,
  onClick: PropTypes.func,
  noAnswerText: PropTypes.string,
  displayLeafValue: PropTypes.bool,
  alternativeTreePregunta: PropTypes.object,
  pathDisplayViewKey: PropTypes.string,
};

export default SelectTreeFormItem;
